import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import whitelabel from "../../whitelabel";
import { Location, useLocation } from "react-router";
import {
	DeviceCategory,
	EventCategory,
	isDeviceLocated,
	LocatedVelavuDevice,
	MeasurementSystem,
	PairedVelavuDevice,
	VelavuAPI,
	VelavuDevice,
	VelavuSite,
} from "velavu-js-api";
import AnalyticsStatType from "../../data/analytics-stat-type";
import { useDateRange } from "../../data/date-range";
import { VelavuModalProvider } from "../../elements/velavu-modal";
import {
	DEFAULT_EVENT_LIST_MAX_ITEMS,
	usePaginatedRemoteSync,
	useSites,
} from "../../helper/api-helper";
import { useCancellableEffect } from "../../helper/hook-helper";
import { classArr } from "../../helper/style-helper";
import { formatTemperature } from "../../helper/unit-helper";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import { AnalyticsGraph } from "./analytics-graph";
import {
	AnalyticsGraphData,
	analyticsGraphDataFromBatteryData,
	analyticsGraphDataFromHumidityData,
	analyticsGraphDataFromTemperatureData,
} from "./analytics-graph-data-helper";
import AnalyticsMap from "./analytics-map";
import AnalyticsSidebar from "./analytics-sidebar";
import AnalyticsStatusBar from "./analytics-status-bar";
import * as styles from "./analytics.module.scss";
import { useProvideContentRefreshable } from "../../elements/refresh-context";
import { AnalyticsTargetTab } from "./analytics-target-tab";

const EMPTY_SITES: VelavuSite[] = [];

export interface AnalyticsLocationState {
	tagID?: string;
	anchorID?: string;
}

export default function Analytics() {
	const { t } = useTranslation();
	const location = useLocation() as Location<AnalyticsLocationState | null>;

	//Load sites
	const { sites, refreshSites } = useSites();
	useProvideContentRefreshable(refreshSites);

	//Keep track of selected site for filtering
	const [selectedSiteID, setSelectedSiteID] = useState<string | undefined>(
		undefined,
	);

	//Load tags
	const [tags, setTags] = useState<PairedVelavuDevice[] | undefined>(
		undefined,
	);
	useCancellableEffect(
		(addPromise) => {
			addPromise(
				VelavuAPI.devices.getAllDevices({
					siteID: selectedSiteID,
					paired: true,
				}),
			)
				.then((result) => {
					setTags(result.data);
				})
				.catch((error) => console.log(error.response));
		},

		[selectedSiteID],
	);
	useProvideContentRefreshable(
		useCallback(() => {
			return VelavuAPI.devices
				.getAllDevices({
					siteID: selectedSiteID,
					paired: true,
				})
				.then((result) => {
					setTags(result.data);
				});
		}, [selectedSiteID, setTags]),
	);
	const assets = useMemo(() => {
		return tags?.map((device) => device.asset);
	}, [tags]);

	//Load anchors
	const [anchors, setAnchors] = useState<VelavuDevice[] | undefined>(
		undefined,
	);
	useCancellableEffect(
		(addPromise) => {
			addPromise(
				VelavuAPI.devices.getAllDevices({
					siteID: selectedSiteID,
					category: DeviceCategory.Anchor,
				}),
			)
				.then((result) => {
					setAnchors(result.data);
				})
				.catch((error) => console.log(error.response));
		},

		[selectedSiteID],
	);
	useProvideContentRefreshable(
		useCallback(() => {
			return VelavuAPI.devices
				.getAllDevices({
					siteID: selectedSiteID,
					category: DeviceCategory.Anchor,
				})
				.then((result) => {
					setAnchors(result.data);
				});
		}, [selectedSiteID, setAnchors]),
	);

	//Update the page title
	useEffect(() => {
		document.title = `${whitelabel.name} | ${t("analytics.page_title")}`;
	}, [t]);

	const userContext = useSignInContext();
	const userPreferences = userContext.preferences;
	const userMeasurementSystem = userPreferences.measurementSystem;

	const [statType, setStatType] = useState(AnalyticsStatType.Temperature);
	const [selectedTab, setSelectedTab] = useState<AnalyticsTargetTab>(() => {
		if (location.state?.anchorID !== undefined) {
			return AnalyticsTargetTab.Anchors;
		} else {
			return AnalyticsTargetTab.Assets;
		}
	});
	const [selectedTagIDs, setSelectedTagIDs] = useState<string[]>(() => {
		const state = location.state;
		if (state?.tagID !== undefined) {
			return [state.tagID];
		} else {
			return [];
		}
	});
	const [selectedAnchorIDs, setSelectedAnchorIDs] = useState<string[]>(() => {
		const state = location.state;
		if (state?.anchorID !== undefined) {
			return [state.anchorID];
		} else {
			return [];
		}
	});
	const [range, setRange] = useDateRange(7);

	const [
		batteryDataAnchors,
		refreshBatteryDataAnchors,
		{ syncing: batteryAnchorsSyncing, hitMaxItems: batteryAnchorsHitMax },
	] = usePaginatedRemoteSync(
		selectedAnchorIDs,
		range,
		DEFAULT_EVENT_LIST_MAX_ITEMS,
		useCallback(
			(deviceID, range, continuationToken) =>
				VelavuAPI.events.getAllEvents(
					{
						deviceID: deviceID,
						category: EventCategory.BatteryLevel,
						since: range.start,
						until: range.end,
					},
					{ continuationToken },
				),
			[],
		),
	);
	useProvideContentRefreshable(refreshBatteryDataAnchors);

	const [
		environmentDataAnchors,
		refreshEnvironmentDataAnchors,
		{ syncing: envAnchorsSyncing, hitMaxItems: envAnchorsHitMax },
	] = usePaginatedRemoteSync(
		selectedAnchorIDs,
		range,
		DEFAULT_EVENT_LIST_MAX_ITEMS,
		useCallback(
			(deviceID, range, continuationToken) =>
				VelavuAPI.events.getAllEvents(
					{
						deviceID: deviceID,
						category: EventCategory.Environment,
						since: range.start,
						until: range.end,
					},
					{ continuationToken },
				),
			[],
		),
	);
	useProvideContentRefreshable(refreshEnvironmentDataAnchors);

	const [
		batteryDataTags,
		refreshBatteryDataTags,
		{ syncing: batteryTagsSyncing, hitMaxItems: batteryTagsHitMax },
	] = usePaginatedRemoteSync(
		selectedTagIDs,
		range,
		DEFAULT_EVENT_LIST_MAX_ITEMS,
		useCallback(
			(deviceID, range, continuationToken) =>
				VelavuAPI.events.getAllEvents(
					{
						deviceID: deviceID,
						category: EventCategory.BatteryLevel,
						since: range.start,
						until: range.end,
					},
					{ continuationToken },
				),
			[],
		),
	);
	useProvideContentRefreshable(refreshBatteryDataTags);

	const [
		environmentDataTags,
		refreshEnvironmentDataTags,
		{ syncing: envTagsSyncing, hitMaxItems: envTagsHitMax },
	] = usePaginatedRemoteSync(
		selectedTagIDs,
		range,
		DEFAULT_EVENT_LIST_MAX_ITEMS,
		useCallback(
			(deviceID, range, continuationToken) =>
				VelavuAPI.events.getAllEvents(
					{
						deviceID: deviceID,
						category: EventCategory.Environment,
						since: range.start,
						until: range.end,
					},
					{ continuationToken },
				),
			[],
		),
	);
	useProvideContentRefreshable(refreshEnvironmentDataTags);

	const graphData = useMemo((): AnalyticsGraphData | undefined => {
		if (selectedTab === AnalyticsTargetTab.Assets) {
			switch (statType) {
				case AnalyticsStatType.Temperature:
					return analyticsGraphDataFromTemperatureData(
						{
							entries: assets,
							measurementSystem: userMeasurementSystem,
							data: environmentDataTags,
						},
						t,
					);
				case AnalyticsStatType.Humidity:
					return analyticsGraphDataFromHumidityData(
						{
							entries: assets,
							measurementSystem: userMeasurementSystem,
							data: environmentDataTags,
						},
						t,
					);
				case AnalyticsStatType.Battery:
					return analyticsGraphDataFromBatteryData(
						{
							entries: assets,
							measurementSystem: userMeasurementSystem,
							data: batteryDataTags,
						},
						t,
					);
			}
		} else {
			switch (statType) {
				case AnalyticsStatType.Temperature:
					return analyticsGraphDataFromTemperatureData(
						{
							entries: anchors,
							measurementSystem: userMeasurementSystem,
							data: environmentDataAnchors,
						},
						t,
					);
				case AnalyticsStatType.Humidity:
					return analyticsGraphDataFromHumidityData(
						{
							entries: anchors,
							measurementSystem: userMeasurementSystem,
							data: environmentDataAnchors,
						},
						t,
					);
				case AnalyticsStatType.Battery:
					return analyticsGraphDataFromBatteryData(
						{
							entries: anchors,
							measurementSystem: userMeasurementSystem,
							data: batteryDataAnchors,
						},
						t,
					);
			}
		}
	}, [
		selectedTab,
		statType,
		assets,
		anchors,
		userMeasurementSystem,
		environmentDataTags,
		environmentDataAnchors,
		batteryDataTags,
		batteryDataAnchors,
		t,
	]);

	const analyticsMapDevices = useMemo((): LocatedVelavuDevice[] => {
		if (selectedTab === AnalyticsTargetTab.Assets) {
			if (tags === undefined) {
				return [];
			}

			return tags
				.filter((device) => isDeviceLocated(device))
				.filter((device) => selectedTagIDs.includes(device.id));
		} else {
			if (anchors === undefined) {
				return [];
			}

			return anchors
				.filter((anchor) => isDeviceLocated(anchor))
				.filter((anchor) => selectedAnchorIDs.includes(anchor.id));
		}
	}, [selectedTab, tags, anchors, selectedTagIDs, selectedAnchorIDs]);

	// Resolve the most recently selected device, used to drive the
	// floor selector / floor override on the map
	const lastSelectedDevice = useMemo((): VelavuDevice | undefined => {
		if (selectedTab === AnalyticsTargetTab.Assets) {
			const lastTagID = selectedTagIDs[selectedTagIDs.length - 1];
			if (lastTagID === undefined) return undefined;
			return tags?.find((device) => device.id === lastTagID);
		} else {
			const lastAnchorID =
				selectedAnchorIDs[selectedAnchorIDs.length - 1];
			if (lastAnchorID === undefined) return undefined;
			return anchors?.find((device) => device.id === lastAnchorID);
		}
	}, [selectedTab, selectedTagIDs, selectedAnchorIDs, tags, anchors]);

	const [
		locationDataTags,
		refreshLocationDataTags,
		{ syncing: locationTagsSyncing, hitMaxItems: locationTagsHitMax },
	] = usePaginatedRemoteSync(
		selectedTagIDs,
		range,
		DEFAULT_EVENT_LIST_MAX_ITEMS,
		useCallback(
			(tagID, rangeArg, continuationToken) =>
				VelavuAPI.events.getAllEvents(
					{
						deviceID: tagID,
						category: EventCategory.Location,
						since: rangeArg.start,
						until: rangeArg.end,
					},
					{ continuationToken },
				),
			[],
		),
	);
	useProvideContentRefreshable(refreshLocationDataTags);

	const [
		locationDataAnchors,
		refreshLocationDataAnchors,
		{ syncing: locationAnchorsSyncing, hitMaxItems: locationAnchorsHitMax },
	] = usePaginatedRemoteSync(
		selectedAnchorIDs,
		range,
		DEFAULT_EVENT_LIST_MAX_ITEMS,
		useCallback(
			(anchorID, rangeArg, continuationToken) =>
				VelavuAPI.events.getAllEvents(
					{
						deviceID: anchorID,
						category: EventCategory.Location,
						since: rangeArg.start,
						until: rangeArg.end,
					},
					{ continuationToken },
				),
			[],
		),
	);
	useProvideContentRefreshable(refreshLocationDataAnchors);

	const mergedLocationData = useMemo(
		() => ({
			...locationDataTags,
			...locationDataAnchors,
		}),
		[locationDataTags, locationDataAnchors],
	);

	// Resolve whether to notify the user that we are syncing based on what
	// data will be visible
	const analyticsSelectionSyncing = useMemo(() => {
		switch (statType) {
			case AnalyticsStatType.Location:
				return locationTagsSyncing || locationAnchorsSyncing;
			case AnalyticsStatType.Battery:
				if (selectedTab === AnalyticsTargetTab.Assets) {
					return batteryTagsSyncing;
				} else {
					return batteryAnchorsSyncing;
				}
			case AnalyticsStatType.Humidity:
			case AnalyticsStatType.Temperature:
				if (selectedTab === AnalyticsTargetTab.Assets) {
					return envTagsSyncing;
				} else {
					return envAnchorsSyncing;
				}
		}
	}, [
		statType,
		selectedTab,
		locationTagsSyncing,
		locationAnchorsSyncing,
		batteryTagsSyncing,
		batteryAnchorsSyncing,
		envTagsSyncing,
		envAnchorsSyncing,
	]);

	const analyticsDataIncomplete =
		locationTagsHitMax ||
		locationAnchorsHitMax ||
		batteryTagsHitMax ||
		batteryAnchorsHitMax ||
		envTagsHitMax ||
		envAnchorsHitMax;

	return (
		<VelavuModalProvider>
			<div className={styles.page}>
				<div className={classArr(styles.card, styles.sidebar)}>
					<AnalyticsSidebar
						sites={sites}
						statType={statType}
						measurementSystem={userMeasurementSystem}
						selectedTab={selectedTab}
						setSelectedTab={setSelectedTab}
						tags={tags}
						selectedTags={selectedTagIDs}
						setSelectedTags={setSelectedTagIDs}
						anchors={anchors}
						selectedAnchors={selectedAnchorIDs}
						setSelectedAnchors={setSelectedAnchorIDs}
						selectedSiteID={selectedSiteID}
						setSelectedSiteID={setSelectedSiteID}
					/>
				</div>
				<div className={styles.content}>
					<div className={styles.card}>
						<AnalyticsStatusBar
							type={statType}
							onChangeType={setStatType}
							selectionCount={
								selectedTab === AnalyticsTargetTab.Assets
									? selectedTagIDs.length
									: selectedAnchorIDs.length
							}
							syncing={analyticsSelectionSyncing}
							dataIncomplete={analyticsDataIncomplete}
						/>
					</div>

					{graphData !== undefined && (
						<div className={classArr(styles.card, styles.cardData)}>
							<AnalyticsGraph
								dateRange={range}
								setDateRange={setRange}
								title={graphData.title}
								unitRange={graphData.unitRange}
								data={graphData.data}
								unitSuffix={graphData.unitSuffix}
							/>
						</div>
					)}

					<div className={classArr(styles.card, styles.cardData)}>
						<AnalyticsMap
							sites={sites ?? EMPTY_SITES}
							stat={statType}
							measurementSystem={userMeasurementSystem}
							devices={analyticsMapDevices}
							lastSelectedDevice={lastSelectedDevice}
							locationData={mergedLocationData}
							dateRange={range}
							setDateRange={setRange}
						/>
					</div>
				</div>
			</div>
		</VelavuModalProvider>
	);
}

export type UnitRange = { min: number; max: number };
export function mapAnalyticsUnitRange(
	stat: AnalyticsStatType,
	measurementSystem?: MeasurementSystem,
): UnitRange | undefined {
	switch (stat) {
		case AnalyticsStatType.Temperature:
			if (measurementSystem) {
				return {
					min: Math.round(formatTemperature(15, measurementSystem)),
					max: Math.round(formatTemperature(30, measurementSystem)),
				};
			} else {
				return { min: 15, max: 30 };
			}
		case AnalyticsStatType.Humidity:
			return { min: 0, max: 100 };
		case AnalyticsStatType.Battery:
			return { min: 0, max: 100 };
		default:
			return undefined;
	}
}
